import { principalsWithPermission } from "../../shared/rbac/crossResourceAvailability";
import type { GrantRow } from "../../shared/rbac/grantQueries";

// The suppliers a space *uses* — the set the server's cross-resource guard
// enforces over. Distinct from usableSuppliersForSpace (the suppliers every
// member can reach, an intersection, for the display cards): a supplier used but
// not reachable-by-all is enforced yet invisible to that intersection, so the
// preflight must compute gaps over "used" to match the server.

// Mirrors spaceSkillEdgesZql: a space uses a skill when any current space
// principal holds skill:read on it directly (a union; a group counts via the
// group's own grant, never through a member's group membership).
export function usedSkillsForSpace<
  T extends { roleGrantsAsScope: readonly GrantRow[] },
>(spaceGrants: readonly GrantRow[], skills: readonly T[]): T[] {
  const spaceUserIds = new Set<string>();
  const spaceGroupIds = new Set<string>();
  const spaceServiceAccountIds = new Set<string>();
  for (const g of spaceGrants) {
    if (g.userId) spaceUserIds.add(g.userId);
    else if (g.userGroupId) spaceGroupIds.add(g.userGroupId);
    else if (g.serviceAccountId) spaceServiceAccountIds.add(g.serviceAccountId);
  }
  if (
    spaceUserIds.size === 0 &&
    spaceGroupIds.size === 0 &&
    spaceServiceAccountIds.size === 0
  )
    return [];
  return skills.filter((skill) => {
    const readers = principalsWithPermission(
      skill.roleGrantsAsScope,
      "skill:read"
    );
    return (
      [...readers.userIds].some((id) => spaceUserIds.has(id)) ||
      [...readers.groupIds].some((id) => spaceGroupIds.has(id)) ||
      [...readers.serviceAccountIds].some((id) =>
        spaceServiceAccountIds.has(id)
      )
    );
  });
}

// Mirrors spaceAppEdgesZql: a space uses a connector when a live branch head in
// the space runs it (`liveAppIdsBySpace`) or it is the space's git remote.
export function usedConnectorsForSpace<
  T extends { id: string; usages?: readonly { type: string; refId: string }[] },
>(
  spaceId: string,
  connectors: readonly T[],
  liveAppIdsBySpace: ReadonlyMap<string, ReadonlySet<string>>
): T[] {
  const live = liveAppIdsBySpace.get(spaceId);
  return connectors.filter(
    (connector) =>
      live?.has(connector.id) ||
      (connector.usages ?? []).some(
        (u) => u.type === "space_git_remote" && u.refId === spaceId
      )
  );
}
